import { useCallback } from 'react';
import { TextField, CommaListField, DateField } from 'framework/ui/field';
import { AppIconLabel } from 'framework/ui/mui-builder';
import VacancyGroupSectionTitle from '../shared/VacancyGroupSectionTitle';
import { EmployeeLinkFieldWithNameLoading, EMPLOYEE_TYPES_ALIAS, EmployeesInput } from 'domain/shared/employee';
import { DATE_FORMAT } from 'framework/utils';
import { ShowSection, IconSectionRow } from 'framework/ui/layout';
import { useShowContext } from 'framework/show';
import { useAuthorization } from 'framework/authorization';
import { RECRUITING_VACANCY_GROUP_UPDATE } from 'domain/shared/newVacancy';
import { RECRUITING_CONTEXT } from 'domain/recruiting/RecruitingContext';
import { Box } from '@mui/material';
import {
  CountryBadge,
  Checkbox,
  useFormShowContext,
  SaveFormShowButton,
  CloseFormShowButton,
  EditFormShowButton,
} from 'framework/form';
import VacancyGroupPositionSlotWarning from './VacancyGroupPositionSlotWarning';

const noBottomMarginSx = { marginBottom: '0 !important' } as const;

const VacancyGroupInfo = () => {
  const { isGranted } = useAuthorization();
  const { isEditMode } = useFormShowContext();
  const { data: show } = useShowContext();
  const showData = show.getData();

  const renderActions = useCallback(() => {
    if (!isGranted(RECRUITING_VACANCY_GROUP_UPDATE, RECRUITING_CONTEXT)) {
      return null;
    }

    return isEditMode ? (
      <>
        <SaveFormShowButton />
        <CloseFormShowButton />
      </>
    ) : (
      <EditFormShowButton />
    );
  }, [isGranted, isEditMode]);

  return (
    <ShowSection
      justifyContent="space-between"
      titleId="vacancy-group-title"
      title={data => <VacancyGroupSectionTitle actions={renderActions()} />}
    >
      <IconSectionRow>
        <TextField source="details.englishLevel" label={<AppIconLabel icon="english" tooltipTitle="English level" />} />
        <Box display="flex" alignItems="center" sx={{ width: '100%' }}>
          <AppIconLabel icon="salary" tooltipTitle="Compensations" />
          {showData?.details?.compensations.length ? (
            showData.details.compensations.map((item: any) => (
              <Box display="flex" alignItems="center" gap={1} sx={{ width: '100%' }}>
                <CountryBadge
                  key={`registration-${item.registration}`}
                  country={item.registration}
                  sx={{ width: '20px', height: '20px' }}
                />
                <Box>
                  {item.compensationCurrencies.map((salary: any) => (
                    <TextField value={salary.value} width="25%" />
                  ))}
                </Box>
              </Box>
            ))
          ) : (
            <TextField />
          )}
        </Box>
        <DateField
          source="details.nearestTargetDate"
          label={<AppIconLabel icon="calendar" tooltipTitle="Nearest target date" />}
          dateFormat={DATE_FORMAT.FULL_DATE}
        />
        <TextField
          source="details.openPositionsCount"
          label={<AppIconLabel icon="baseline-people" tooltipTitle="Open positions" />}
        />
        <CommaListField label={<AppIconLabel icon="work" tooltipTitle="Projects" />} source="details.projectNames" />
        {isEditMode
          ? [
              <EmployeesInput
                source="sourcers"
                label={<AppIconLabel icon="s-user" tooltipTitle="Sourcers" />}
                sx={noBottomMarginSx}
                customMapper={(item: any) => ({
                  value: item.alias,
                  label: `${item.nameEn?.firstName ?? ''} ${item.nameEn?.lastName ?? ''}`,
                })}
              />,

              <EmployeesInput
                source="details.coordinators"
                label={<AppIconLabel icon="cs-user" tooltipTitle="Coordinators" />}
                sx={noBottomMarginSx}
                customMapper={(item: any) => ({
                  value: item.alias,
                  label: `${item.nameEn?.firstName ?? ''} ${item.nameEn?.lastName ?? ''}`,
                })}
                isAdvanced
                defaultFilter={{
                  filter: [
                    {
                      filterType: 'FILTER_GROUP',
                      condition: 'OR',
                      filters: [
                        {
                          filterType: 'FILTER',
                          property: 'specification:peopleManagerSpecification',
                          operator: 'EQ',
                          value: true,
                        },
                        {
                          filterType: 'FILTER',
                          property: 'specification:isStaffingCoordinator',
                          operator: 'EQ',
                          value: true,
                        },
                      ],
                    },
                    {
                      filterType: 'FILTER',
                      property: 'employeeType',
                      operator: 'NE',
                      value: EMPLOYEE_TYPES_ALIAS.CONTRACTOR,
                    },
                    {
                      filterType: 'FILTER',
                      property: 'archivedStatus',
                      operator: 'EQ',
                      value: 'ACTIVE',
                    },
                  ],
                }}
              />,

              <EmployeesInput
                source="recruiters"
                label={<AppIconLabel icon="r-user" tooltipTitle="Recruiters" />}
                sx={noBottomMarginSx}
                customMapper={(item: any) => ({
                  value: item.alias,
                  label: `${item.nameEn?.firstName ?? ''} ${item.nameEn?.lastName ?? ''}`,
                })}
                defaultFilter={{ 'specification:isRecruiter': { eq: true } }}
              />,

              <Checkbox label="Hide candidates" source="isCandidatesHidden" sx={noBottomMarginSx} />,
              <Checkbox
                label="Disable decision auto transition"
                source="isAutoDecisionTransitionDisabled"
                sx={noBottomMarginSx}
              />,
            ]
          : [
              <CommaListField
                label={<AppIconLabel icon="s-user" tooltipTitle="Sourcers" />}
                source="sourcers"
                isNeedJoin={false}
                mapValue={value => <EmployeeLinkFieldWithNameLoading label="" externalId={value} />}
              />,

              <CommaListField
                label={<AppIconLabel icon="cs-user" tooltipTitle="Coordinators" />}
                source="details.coordinators"
                isNeedJoin={false}
                mapValue={value => <EmployeeLinkFieldWithNameLoading label="" externalId={value} />}
              />,

              <CommaListField
                label={<AppIconLabel icon="r-user" tooltipTitle="Recruiters" />}
                source="recruiters"
                isNeedJoin={false}
                mapValue={value => <EmployeeLinkFieldWithNameLoading label="" externalId={value} />}
              />,

              <Checkbox label="Hide candidates" source="isCandidatesHidden" sx={noBottomMarginSx} disabled />,
              <Checkbox
                label="Disable decision auto transition"
                source="isAutoDecisionTransitionDisabled"
                sx={noBottomMarginSx}
                disabled
              />,
            ]}
      </IconSectionRow>
      <VacancyGroupPositionSlotWarning />
    </ShowSection>
  );
};

export default VacancyGroupInfo;
